import { useState } from 'react';
import { api, can, getUser } from '../lib/api';
import { Card, Empty, ErrorNote, Field, Loading, Modal, PageHead, Pill, useResource, useToast } from '../components/ui';
import { retention } from '../lib/format';

/**
 * Data items: the catalogue behind the data categories each purpose names.
 * One row per item: sensitivity, retention limit, the purposes that use it,
 * and whether someone has reviewed it. Items typed on a purpose arrive with a
 * suggested sensitivity and wait for review.
 */

type Sensitivity = 'low' | 'medium' | 'high';

interface Item {
  id: string;
  key: string;
  name: string;
  translations: Record<string, string>;
  description: string;
  sensitivity: Sensitivity;
  retentionLimitDays: number | null;
  source: 'console' | 'purpose' | 'migration';
  reviewed: boolean;
  reviewedBy: string | null;
  purposes: Array<{ id: string; name: string; status: string; retentionDays: number | null; tracking: boolean }>;
}

interface Listing {
  items: Item[];
  issues: Array<{ purposeId: string; purpose: string; issues: Array<{ rule: string; message: string }> }>;
}

const SENSITIVITY_LABEL: Record<Sensitivity, string> = { low: 'Low', medium: 'Medium', high: 'High' };
const SENSITIVITY_TONE: Record<Sensitivity, 'bad' | 'warn' | undefined> = { low: undefined, medium: 'warn', high: 'bad' };

const RULES = [
  'A purpose using a high-sensitivity item states a retention period.',
  'A purpose keeps no item longer than the item\'s retention limit.',
  'A high-sensitivity item is not used for tracking, behavioural monitoring or targeted advertising.',
  'A tracking or targeted advertising purpose is not offered to children (S.9(3)).',
];

export default function DataItems() {
  const user = getUser();
  const writable = can(user, 'purposes:edit');
  const toast = useToast();
  const [filter, setFilter] = useState<'all' | 'review' | Sensitivity>('all');
  const [editing, setEditing] = useState<Item | 'new' | null>(null);
  const data = useResource<Listing>(() => api.get('/v1/data-items'));
  const items = data.data?.items ?? [];
  const shown = items.filter((i) => filter === 'all' || (filter === 'review' ? !i.reviewed : i.sensitivity === filter));
  const toReview = items.filter((i) => !i.reviewed).length;

  return (
    <>
      <PageHead title="Data items">
        {writable && <button className="primary" onClick={() => setEditing('new')}>New data item</button>}
      </PageHead>
      <ErrorNote error={data.error} />

      {data.data && data.data.issues.length > 0 && (
        <div className="note warn" style={{ marginBottom: 12 }}>
          <strong>{data.data.issues.length} {data.data.issues.length === 1 ? 'purpose fails' : 'purposes fail'} the data rules.</strong> The next save of each purpose applies them.
          <ul style={{ margin: '6px 0 0 18px' }}>
            {data.data.issues.map((p) => (
              <li key={p.purposeId}>{p.purpose}: {p.issues.map((i) => i.message).join(' ')}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="tabs">
        {(['all', 'review', 'high', 'medium', 'low'] as const).map((f) => (
          <button key={f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f === 'review' ? 'Not reviewed' : SENSITIVITY_LABEL[f]}
            {f === 'review' && toReview ? <span className="tab-count">{toReview}</span> : null}
          </button>
        ))}
      </div>

      <Card>
        {data.loading && !data.data && <Loading what="Loading data items" />}
        {data.data && shown.length === 0 && <Empty>No data items.</Empty>}
        {shown.length > 0 && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr><th>Data item</th><th>Sensitivity</th><th>Retention limit</th><th>Used by</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {shown.map((i) => (
                  <tr key={i.id}>
                    <td title={i.description || undefined}>
                      <strong>{i.name}</strong>
                      {i.description && <div className="small muted">{i.description}</div>}
                    </td>
                    <td><Pill tone={SENSITIVITY_TONE[i.sensitivity]}>{SENSITIVITY_LABEL[i.sensitivity]}</Pill></td>
                    <td className="small">{i.retentionLimitDays ? retention(i.retentionLimitDays) : '—'}</td>
                    <td className="small text-list">{i.purposes.length ? i.purposes.map((p) => p.name).join(', ') : <span className="muted">No purpose</span>}</td>
                    <td className="small">{i.reviewed ? 'Reviewed' : <span className="bad-text">Not reviewed</span>}</td>
                    <td className="right">{writable && <button className="small" onClick={() => setEditing(i)}>{i.reviewed ? 'Edit' : 'Review'}</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="small muted" style={{ marginTop: 12 }}>
        Rules applied when a purpose or a data item is saved:
        <ol style={{ margin: '4px 0 0 18px' }}>{RULES.map((r) => <li key={r}>{r}</li>)}</ol>
      </div>

      {editing && (
        <ItemEditor
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(m) => { setEditing(null); toast(m, 'ok'); data.reload(); }}
        />
      )}
    </>
  );
}

function ItemEditor({ item, onClose, onSaved }: { item: Item | null; onClose: () => void; onSaved: (message: string) => void }) {
  const [name, setName] = useState(item?.name ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [sensitivity, setSensitivity] = useState<Sensitivity>(item?.sensitivity ?? 'low');
  const [limit, setLimit] = useState<string>(item?.retentionLimitDays ? String(item.retentionLimitDays) : '');
  const [translations, setTranslations] = useState<string>(
    Object.entries(item?.translations ?? {}).map(([k, v]) => `${k}: ${v}`).join('\n'),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const used = (item?.purposes.length ?? 0) > 0;

  function pick(s: Sensitivity) {
    setSensitivity(s);
    // High sensitivity starts from a one-year limit; clear it to keep none.
    if (s === 'high' && !limit && !item?.retentionLimitDays) setLimit('365');
  }

  async function save() {
    setBusy(true);
    setError(null);
    const tr: Record<string, string> = {};
    for (const line of translations.split('\n')) {
      const m = /^\s*([a-z]{2,3})\s*:\s*(.+)$/.exec(line);
      if (m) tr[m[1]!] = m[2]!.trim();
    }
    const body = { name: name.trim(), description, sensitivity, retentionLimitDays: limit ? Number(limit) : null, translations: tr };
    try {
      if (item) await api.put(`/v1/data-items/${item.id}`, body);
      else await api.post('/v1/data-items', body);
      onSaved(item ? 'Data item saved.' : 'Data item added.');
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={item ? `Data item: ${item.name}` : 'New data item'}
      onClose={onClose}
      footer={<><button onClick={onClose} disabled={busy}>Cancel</button><button className="primary" onClick={save} disabled={busy || !name.trim()}>{busy ? 'Saving' : 'Save'}</button></>}
    >
      <Field label="Name" help={used ? 'Used by a purpose: rename the category on the purpose instead.' : 'Matched to the data categories purposes name, ignoring case.'}>
        <input type="text" value={name} disabled={used} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Description">
        <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="row">
        <Field label="Sensitivity">
          <select value={sensitivity} onChange={(e) => pick(e.target.value as Sensitivity)}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </Field>
        <Field label="Retention limit (days)" help="Empty: no limit beyond the purpose.">
          <input type="number" min={1} value={limit} onChange={(e) => setLimit(e.target.value)} />
        </Field>
      </div>
      <Field label="Name in other languages" help="One per line, as language code and name. For example, hi: नाम">
        <textarea rows={3} value={translations} onChange={(e) => setTranslations(e.target.value)} />
      </Field>
      {item && item.purposes.length > 0 && (
        <div className="small muted">Used by {item.purposes.map((p) => `${p.name} (${retention(p.retentionDays)})`).join(', ')}.</div>
      )}
      <ErrorNote error={error} />
    </Modal>
  );
}
